import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Grid,
  CardContent,
  CardActions,
  CardHeader,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  MenuItem,
  IconButton,
  Divider,
  Badge,
  Tab,
} from '@mui/material';
import { leaderboardAPI, usersAPI } from '../../services/api';
import { useTranslation } from 'react-i18next';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import SportsKabaddiIcon from '@mui/icons-material/SportsKabaddi';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import DateRangeIcon from '@mui/icons-material/DateRange';
import { format } from 'date-fns';
import { ru, enGB } from 'date-fns/locale';
import Button from '../UIKit/Button';
import Card from '../UIKit/Card';
import Tabs from '../UIKit/Tabs';
import TextField from '../UIKit/TextField';
import Select from '../UIKit/Select';
import { getUserAvatarSrc } from '../../utils/avatarUtils';

const Challenges = ({ currentUser }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [challenges, setChallenges] = useState([]);
  const [users, setUsers] = useState([]);
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0);
  const [respondingChallengeId, setRespondingChallengeId] = useState(null);

  const [newChallenge, setNewChallenge] = useState({
    receiverId: '',
    type: 'xp',
    goal: 100,
    duration: 7,
    description: '',
  });

  const [formErrors, setFormErrors] = useState({});

  const { t, i18n } = useTranslation(['leaderboard', 'common']);

  // Функция для получения локали для date-fns
  const getDateLocale = () => {
    return i18n.language === 'ru' ? ru : enGB;
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Загружаем все вызовы пользователя
        const challengesResponse = await leaderboardAPI.getUserChallenges();
        setChallenges(challengesResponse.data.challenges);

        // Загружаем список пользователей для выбора получателя
        const usersResponse = await usersAPI.getAll();
        setUsers(
          (usersResponse.data.users || []).filter(
            (user) => user._id !== currentUser?._id
          )
        );
      } catch (err) {
        console.error('Ошибка при загрузке вызовов:', err);
        setError(t('leaderboard:challenges.errorLoading'));
      } finally {
        setLoading(false);
      }
    };

    if (currentUser) {
      fetchData();
    }
  }, [currentUser, t]);

  const handleCreateDialogOpen = () => {
    setOpenCreateDialog(true);
  };

  const handleCreateDialogClose = () => {
    setOpenCreateDialog(false);
    setFormErrors({});
  };

  const validateForm = () => {
    const errors = {};

    if (!newChallenge.receiverId) {
      errors.receiverId = t(
        'leaderboard:challenges.form.validation.selectOpponent'
      );
    }

    if (!newChallenge.goal || newChallenge.goal <= 0) {
      errors.goal = t('leaderboard:challenges.form.validation.goalRequired');
    }

    if (!newChallenge.duration || newChallenge.duration < 1) {
      errors.duration = t(
        'leaderboard:challenges.form.validation.durationRequired'
      );
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreateChallenge = async () => {
    if (!validateForm()) return;

    try {
      setLoading(true);

      // Преобразуем данные для соответствия API сервера
      const endDate = new Date();
      endDate.setDate(endDate.getDate() + parseInt(newChallenge.duration));

      const challengeData = {
        challengedId: newChallenge.receiverId,
        type: newChallenge.type,
        goal: parseInt(newChallenge.goal),
        endDate: endDate.toISOString(),
      };

      await leaderboardAPI.createChallenge(challengeData);

      // Перезагрузка списка вызовов
      const response = await leaderboardAPI.getUserChallenges();
      setChallenges(response.data.challenges);

      // Закрытие диалога и сброс формы
      setOpenCreateDialog(false);
      setNewChallenge({
        receiverId: '',
        type: 'xp',
        goal: 100,
        duration: 7,
        description: '',
      });
    } catch (err) {
      console.error('Ошибка при создании вызова:', err);
      setError(t('leaderboard:challenges.errorCreating'));
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewChallenge((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Очистка ошибки при вводе
    if (formErrors[name]) {
      setFormErrors((prev) => ({
        ...prev,
        [name]: null,
      }));
    }
  };

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const handleRespondToChallenge = async (challengeId, response) => {
    try {
      setRespondingChallengeId(challengeId);

      await leaderboardAPI.respondToChallenge(challengeId, response);

      // Обновляем список вызовов после ответа
      const challengesResponse = await leaderboardAPI.getUserChallenges();
      setChallenges(challengesResponse.data.challenges);
    } catch (err) {
      console.error(
        `Ошибка при ${
          response === true
            ? 'принятии'
            : response === false
            ? 'отклонении'
            : 'отмене'
        } вызова:`,
        err
      );
      setError(t('leaderboard:challenges.errorResponding'));
    } finally {
      setRespondingChallengeId(null);
    }
  };

  // Функция для получения статуса вызова
  const getChallengeStatus = (challenge) => {
    if (challenge.status === 'pending') {
      return {
        label: t('leaderboard:challenges.status.pending'),
        color: 'warning',
      };
    } else if (challenge.status === 'active') {
      return {
        label: t('leaderboard:challenges.status.active'),
        color: 'success',
      };
    } else if (challenge.status === 'completed') {
      return {
        label: t('leaderboard:challenges.status.completed'),
        color: 'info',
      };
    } else if (challenge.status === 'rejected') {
      return {
        label: t('leaderboard:challenges.status.rejected'),
        color: 'error',
      };
    } else {
      return {
        label: t('leaderboard:challenges.status.cancelled'),
        color: 'default',
      };
    }
  };

  // Функция для отображения типа вызова
  const getChallengeType = (type) => {
    switch (type) {
      case 'xp':
        return t('leaderboard:challenges.types.xp');
      case 'courses':
        return t('leaderboard:challenges.types.courses');
      case 'tests':
        return t('leaderboard:challenges.types.tests');
      default:
        return t('common:unknown');
    }
  };

  if (loading && challenges.length === 0) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="300px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error && challenges.length === 0) {
    return (
      <Box my={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  // Разделяем вызовы на входящие, исходящие и активные
  const incomingChallenges = challenges.filter(
    (challenge) =>
      challenge.receiverId === currentUser?._id &&
      challenge.status === 'pending'
  );

  const outgoingChallenges = challenges.filter(
    (challenge) =>
      challenge.senderId === currentUser?._id && challenge.status === 'pending'
  );

  const activeChallenges = challenges.filter(
    (challenge) => challenge.status === 'active'
  );

  const completedChallenges = challenges.filter(
    (challenge) =>
      challenge.status === 'completed' ||
      challenge.status === 'rejected' ||
      challenge.status === 'cancelled'
  );

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h5" display="flex" alignItems="center" gap={1}>
          <SportsKabaddiIcon />
          {t('leaderboard:challenges.title')}
        </Typography>

        <Button
          variant="contained"
          color="primary"
          startIcon={<PersonAddIcon />}
          onClick={handleCreateDialogOpen}
        >
          {t('leaderboard:challenges.create')}
        </Button>
      </Box>

      <Box
        sx={{
          bgcolor: 'background.paper',
          borderRadius: { xs: '12px', sm: '16px' },
          p: '4px',
          width: '100%',
          overflow: 'hidden',
          mb: 3,
        }}
      >
        <Tabs
          value={selectedTab}
          onChange={handleTabChange}
          swapBackground
        >
          <Tab
            label={
              <Box display="flex" alignItems="center" gap={1}>
                {incomingChallenges.length > 0 && (
                  <Badge color="error" badgeContent={incomingChallenges.length}>
                    <span>{t('leaderboard:challenges.tabs.incoming')}</span>
                  </Badge>
                )}
                {incomingChallenges.length === 0 && (
                  <span>{t('leaderboard:challenges.tabs.incoming')}</span>
                )}
              </Box>
            }
          />
          <Tab label={t('leaderboard:challenges.tabs.outgoing')} />
          <Tab label={t('leaderboard:challenges.tabs.active')} />
          <Tab label={t('leaderboard:challenges.tabs.completed')} />
        </Tabs>
      </Box>

      {/* Вкладка 1: Входящие вызовы */}
      {selectedTab === 0 && (
        <Box>
          {incomingChallenges.length === 0 ? (
            <Paper
              elevation={0}
              sx={{ borderRadius: '16px', p: 3, textAlign: 'center' }}
            >
              <Typography variant="h6" color="text.secondary">
                {t('leaderboard:challenges.noIncoming')}
              </Typography>
            </Paper>
          ) : (
            <Grid container spacing={3}>
              {incomingChallenges.map((challenge) => {
                const sender =
                  users.find((user) => user._id === challenge.senderId) || {};

                return (
                  <Grid item key={challenge._id} xs={12} md={6}>
                    <Card>
                      <CardHeader
                        avatar={
                          <Avatar src={getUserAvatarSrc(sender)}>
                            {sender.username?.charAt(0) || 'U'}
                          </Avatar>
                        }
                        title={`${t('leaderboard:challenges.challengeFrom')} ${
                          sender.username ||
                          t('leaderboard:challenges.opponent')
                        }`}
                        subheader={
                          <Box
                            display="flex"
                            alignItems="center"
                            gap={1}
                            mt={0.5}
                          >
                            <Chip
                              label={getChallengeStatus(challenge).label}
                              color={getChallengeStatus(challenge).color}
                              size="small"
                            />
                            <Chip
                              label={getChallengeType(challenge.type)}
                              variant="outlined"
                              size="small"
                            />
                          </Box>
                        }
                      />

                      <CardContent>
                        {challenge.description && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            mb={2}
                          >
                            {challenge.description}
                          </Typography>
                        )}

                        <Box display="flex" alignItems="center" mb={1}>
                          <DateRangeIcon
                            fontSize="small"
                            sx={{ mr: 1, color: 'text.secondary' }}
                          />
                          <Typography variant="body2" color="text.secondary">
                            {t('leaderboard:challenges.duration')}:{' '}
                            {challenge.duration ||
                              t('common:unknown').toLowerCase()}{' '}
                            {t('leaderboard:challenges.days')}
                          </Typography>
                        </Box>

                        <Box mb={1}>
                          <Typography variant="body2" fontWeight="bold">
                            {t('leaderboard:challenges.goal')}: {challenge.goal}{' '}
                            {challenge.type === 'xp'
                              ? 'XP'
                              : challenge.type === 'courses'
                              ? t(
                                  'leaderboard:challenges.types.courses'
                                ).toLowerCase()
                              : t(
                                  'leaderboard:challenges.types.tests'
                                ).toLowerCase()}
                          </Typography>
                        </Box>
                      </CardContent>

                      <CardActions>
                        <Button
                          color="success"
                          variant="text"
                          startIcon={<CheckIcon />}
                          onClick={() =>
                            handleRespondToChallenge(challenge._id, true)
                          }
                          disabled={respondingChallengeId === challenge._id}
                        >
                          {t('leaderboard:challenges.accept')}
                        </Button>
                        <Button
                          color="error"
                          variant="text"
                          startIcon={<CloseIcon />}
                          onClick={() =>
                            handleRespondToChallenge(challenge._id, false)
                          }
                          disabled={respondingChallengeId === challenge._id}
                        >
                          {t('leaderboard:challenges.reject')}
                        </Button>
                      </CardActions>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </Box>
      )}

      {/* Вкладка 2: Исходящие вызовы */}
      {selectedTab === 1 && (
        <Box>
          {outgoingChallenges.length === 0 ? (
            <Paper
              elevation={0}
              sx={{ borderRadius: '16px', p: 3, textAlign: 'center' }}
            >
              <Typography variant="h6" color="text.secondary">
                {t('leaderboard:challenges.noOutgoing')}
              </Typography>
            </Paper>
          ) : (
            <Grid container spacing={3}>
              {outgoingChallenges.map((challenge) => {
                const receiver =
                  users.find((user) => user._id === challenge.receiverId) || {};

                return (
                  <Grid item key={challenge._id} xs={12} md={6}>
                    <Card>
                      <CardHeader
                        avatar={
                          <Avatar src={getUserAvatarSrc(receiver)}>
                            {receiver.username?.charAt(0) || 'U'}
                          </Avatar>
                        }
                        title={`${t('leaderboard:challenges.challengeFor')} ${
                          receiver.username ||
                          t('leaderboard:challenges.opponent')
                        }`}
                        subheader={
                          <Box
                            display="flex"
                            alignItems="center"
                            gap={1}
                            mt={0.5}
                          >
                            <Chip
                              label={getChallengeStatus(challenge).label}
                              color={getChallengeStatus(challenge).color}
                              size="small"
                            />
                            <Chip
                              label={getChallengeType(challenge.type)}
                              variant="outlined"
                              size="small"
                            />
                          </Box>
                        }
                      />

                      <CardContent>
                        {challenge.description && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            mb={2}
                          >
                            {challenge.description}
                          </Typography>
                        )}

                        <Box display="flex" alignItems="center" mb={1}>
                          <DateRangeIcon
                            fontSize="small"
                            sx={{ mr: 1, color: 'text.secondary' }}
                          />
                          <Typography variant="body2" color="text.secondary">
                            {t('leaderboard:challenges.duration')}:{' '}
                            {challenge.duration ||
                              t('common:unknown').toLowerCase()}{' '}
                            {t('leaderboard:challenges.days')}
                          </Typography>
                        </Box>

                        <Box mb={1}>
                          <Typography variant="body2" fontWeight="bold">
                            {t('leaderboard:challenges.goal')}: {challenge.goal}{' '}
                            {challenge.type === 'xp'
                              ? 'XP'
                              : challenge.type === 'courses'
                              ? t(
                                  'leaderboard:challenges.types.courses'
                                ).toLowerCase()
                              : t(
                                  'leaderboard:challenges.types.tests'
                                ).toLowerCase()}
                          </Typography>
                        </Box>
                      </CardContent>

                      <CardActions>
                        <Button
                          color="error"
                          variant="text"
                          startIcon={<CloseIcon />}
                          onClick={() =>
                            handleRespondToChallenge(challenge._id, 'cancel')
                          }
                          disabled={respondingChallengeId === challenge._id}
                        >
                          {t('leaderboard:challenges.cancel')}
                        </Button>
                      </CardActions>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </Box>
      )}

      {/* Вкладка 3: Активные вызовы */}
      {selectedTab === 2 && (
        <Box>
          {activeChallenges.length === 0 ? (
            <Paper
              elevation={0}
              sx={{ borderRadius: '16px', p: 3, textAlign: 'center' }}
            >
              <Typography variant="h6" color="text.secondary">
                {t('leaderboard:challenges.noActive')}
              </Typography>
            </Paper>
          ) : (
            <Grid container spacing={3}>
              {activeChallenges.map((challenge) => {
                const opponent =
                  users.find(
                    (user) =>
                      user._id ===
                      (challenge.senderId === currentUser?._id
                        ? challenge.receiverId
                        : challenge.senderId)
                  ) || {};

                // Определяем прогресс
                const currentUserProgress =
                  challenge.senderId === currentUser?._id
                    ? challenge.senderProgress || 0
                    : challenge.receiverProgress || 0;

                const opponentProgress =
                  challenge.senderId === currentUser?._id
                    ? challenge.receiverProgress || 0
                    : challenge.senderProgress || 0;

                const currentUserPercent = Math.min(
                  100,
                  (currentUserProgress / challenge.goal) * 100
                );
                const opponentPercent = Math.min(
                  100,
                  (opponentProgress / challenge.goal) * 100
                );

                return (
                  <Grid item key={challenge._id} xs={12}>
                    <Card>
                      <CardHeader
                        title={`${t('leaderboard:challenges.challengeFrom')} ${
                          opponent.username ||
                          t('leaderboard:challenges.opponent')
                        }`}
                        subheader={
                          <Box
                            display="flex"
                            alignItems="center"
                            gap={1}
                            mt={0.5}
                          >
                            <Chip
                              label={getChallengeStatus(challenge).label}
                              color={getChallengeStatus(challenge).color}
                              size="small"
                            />
                            <Chip
                              label={getChallengeType(challenge.type)}
                              variant="outlined"
                              size="small"
                            />
                          </Box>
                        }
                      />

                      <CardContent>
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                          mb={3}
                        >
                          <Box flex={1} pr={2} textAlign="center">
                            <Avatar
                              src={currentUser?.avatar}
                              sx={{ width: 60, height: 60, mx: 'auto', mb: 1 }}
                            >
                              {currentUser?.username?.charAt(0) || 'U'}
                            </Avatar>
                            <Typography variant="subtitle1" fontWeight="bold">
                              {t('leaderboard:challenges.you')}
                            </Typography>
                            <Typography
                              variant="body2"
                              fontWeight="bold"
                              color="primary"
                            >
                              {currentUserProgress} / {challenge.goal}
                            </Typography>
                          </Box>

                          <Box
                            sx={{
                              bgcolor: 'background.default',
                              borderRadius: '50%',
                              width: 40,
                              height: 40,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <CompareArrowsIcon color="action" />
                          </Box>

                          <Box flex={1} pl={2} textAlign="center">
                            <Avatar
                              src={opponent.avatar}
                              sx={{ width: 60, height: 60, mx: 'auto', mb: 1 }}
                            >
                              {opponent.username?.charAt(0) || 'U'}
                            </Avatar>
                            <Typography variant="subtitle1" fontWeight="bold">
                              {opponent.username ||
                                t('leaderboard:challenges.opponent')}
                            </Typography>
                            <Typography
                              variant="body2"
                              fontWeight="bold"
                              color="primary"
                            >
                              {opponentProgress} / {challenge.goal}
                            </Typography>
                          </Box>
                        </Box>

                        <Box>
                          <Typography variant="body2" mb={0.5}>
                            {t('leaderboard:challenges.yourProgress')}:
                          </Typography>
                          <Box
                            sx={{
                              bgcolor: 'background.default',
                              borderRadius: 1,
                              height: 10,
                              position: 'relative',
                              overflow: 'hidden',
                              mb: 2,
                            }}
                          >
                            <Box
                              sx={{
                                bgcolor: 'primary.main',
                                position: 'absolute',
                                left: 0,
                                top: 0,
                                bottom: 0,
                                width: `${currentUserPercent}%`,
                                transition: 'width 0.3s ease-in-out',
                              }}
                            />
                          </Box>

                          <Typography variant="body2" mb={0.5}>
                            {t('leaderboard:challenges.opponentProgress')}:
                          </Typography>
                          <Box
                            sx={{
                              bgcolor: 'background.default',
                              borderRadius: 1,
                              height: 10,
                              position: 'relative',
                              overflow: 'hidden',
                            }}
                          >
                            <Box
                              sx={{
                                bgcolor: 'secondary.main',
                                position: 'absolute',
                                left: 0,
                                top: 0,
                                bottom: 0,
                                width: `${opponentPercent}%`,
                                transition: 'width 0.3s ease-in-out',
                              }}
                            />
                          </Box>
                        </Box>

                        <Box display="flex" alignItems="center" mt={2}>
                          <HourglassEmptyIcon
                            fontSize="small"
                            sx={{ mr: 1, color: 'text.secondary' }}
                          />
                          <Typography variant="body2" color="text.secondary">
                            {t('leaderboard:challenges.daysLeft')}:{' '}
                            {(() => {
                              if (challenge.endDate) {
                                const endDate = new Date(challenge.endDate);
                                const now = new Date();
                                const diffTime = endDate - now;
                                const diffDays = Math.ceil(
                                  diffTime / (1000 * 60 * 60 * 24)
                                );
                                return diffDays > 0 ? diffDays : 0;
                              }
                              return t('common:unknown').toLowerCase();
                            })()}
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </Box>
      )}

      {/* Вкладка 4: Завершенные вызовы */}
      {selectedTab === 3 && (
        <Box>
          {completedChallenges.length === 0 ? (
            <Paper
              elevation={0}
              sx={{ borderRadius: '16px', p: 3, textAlign: 'center' }}
            >
              <Typography variant="h6" color="text.secondary">
                {t('leaderboard:challenges.noCompleted')}
              </Typography>
            </Paper>
          ) : (
            <Grid container spacing={3}>
              {completedChallenges.map((challenge) => {
                const opponent =
                  users.find(
                    (user) =>
                      user._id ===
                      (challenge.senderId === currentUser?._id
                        ? challenge.receiverId
                        : challenge.senderId)
                  ) || {};

                // Определяем победителя и проигравшего
                let result;

                if (challenge.status === 'rejected') {
                  result =
                    challenge.senderId === currentUser?._id
                      ? t('leaderboard:challenges.results.rejected')
                      : t('leaderboard:challenges.results.youRejected');
                } else {
                  const currentUserProgress =
                    challenge.senderId === currentUser?._id
                      ? challenge.senderProgress || 0
                      : challenge.receiverProgress || 0;

                  const opponentProgress =
                    challenge.senderId === currentUser?._id
                      ? challenge.receiverProgress || 0
                      : challenge.senderProgress || 0;

                  if (
                    currentUserProgress >= challenge.goal &&
                    opponentProgress >= challenge.goal
                  ) {
                    result = t('leaderboard:challenges.results.draw');
                  } else if (currentUserProgress >= challenge.goal) {
                    result = t('leaderboard:challenges.results.youWon');
                  } else if (opponentProgress >= challenge.goal) {
                    result = t('leaderboard:challenges.results.youLost');
                  } else {
                    result = t('leaderboard:challenges.results.noWinner');
                  }
                }

                return (
                  <Grid item key={challenge._id} xs={12} md={6}>
                    <Card>
                      <CardHeader
                        title={`${t('leaderboard:challenges.challengeFrom')} ${
                          opponent.username ||
                          t('leaderboard:challenges.opponent')
                        }`}
                        subheader={
                          <Box
                            display="flex"
                            alignItems="center"
                            gap={1}
                            mt={0.5}
                          >
                            <Chip
                              label={getChallengeStatus(challenge).label}
                              color={getChallengeStatus(challenge).color}
                              size="small"
                            />
                            <Chip
                              label={getChallengeType(challenge.type)}
                              variant="outlined"
                              size="small"
                            />
                          </Box>
                        }
                      />

                      <CardContent>
                        <Typography
                          variant="h6"
                          align="center"
                          fontWeight="bold"
                          color={
                            result ===
                            t('leaderboard:challenges.results.youWon')
                              ? 'success.main'
                              : result ===
                                t('leaderboard:challenges.results.youLost')
                              ? 'error.main'
                              : 'text.primary'
                          }
                          mb={2}
                        >
                          {result}
                        </Typography>

                        {challenge.status !== 'rejected' && (
                          <>
                            <Divider sx={{ my: 2 }} />

                            <Box
                              display="flex"
                              justifyContent="space-between"
                              mb={1}
                            >
                              <Typography variant="body2">
                                {t('leaderboard:challenges.results.yourResult')}
                                :
                              </Typography>
                              <Typography variant="body2" fontWeight="bold">
                                {challenge.senderId === currentUser?._id
                                  ? challenge.senderProgress || 0
                                  : challenge.receiverProgress || 0}{' '}
                                / {challenge.goal}
                              </Typography>
                            </Box>

                            <Box display="flex" justifyContent="space-between">
                              <Typography variant="body2">
                                {t(
                                  'leaderboard:challenges.results.opponentResult'
                                )}
                                :
                              </Typography>
                              <Typography variant="body2" fontWeight="bold">
                                {challenge.senderId === currentUser?._id
                                  ? challenge.receiverProgress || 0
                                  : challenge.senderProgress || 0}{' '}
                                / {challenge.goal}
                              </Typography>
                            </Box>
                          </>
                        )}

                        <Box mt={2} display="flex" alignItems="center">
                          <DateRangeIcon
                            fontSize="small"
                            sx={{ mr: 1, color: 'text.secondary' }}
                          />
                          <Typography variant="body2" color="text.secondary">
                            {format(
                              new Date(
                                challenge.completedAt || challenge.updatedAt
                              ),
                              'dd MMMM yyyy',
                              { locale: getDateLocale() }
                            )}
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </Box>
      )}

      {/* Диалоговое окно создания вызова */}
      <Dialog
        open={openCreateDialog}
        onClose={handleCreateDialogClose}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: '16px',
          },
        }}
      >
        <DialogTitle>
          {t('leaderboard:challenges.form.title')}
          <IconButton
            onClick={handleCreateDialogClose}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          <Box component="form" sx={{ mt: 1 }}>
            <FormControl
              fullWidth
              margin="normal"
              required
              error={!!formErrors.receiverId}
            >
              <Select
                labelId="receiver-label"
                id="receiverId"
                name="receiverId"
                value={newChallenge.receiverId}
                label={t('leaderboard:challenges.form.selectOpponent')}
                onChange={handleInputChange}
              >
                {users.map((user) => (
                  <MenuItem key={user._id} value={user._id}>
                    <Box display="flex" alignItems="center">
                      <Avatar
                        src={user.avatar}
                        sx={{ width: 24, height: 24, mr: 1 }}
                      >
                        {user.username?.charAt(0)}
                      </Avatar>
                      {user.username}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
              {formErrors.receiverId && (
                <Typography variant="caption" color="error">
                  {t('leaderboard:challenges.form.validation.selectOpponent')}
                </Typography>
              )}
            </FormControl>

            <FormControl fullWidth margin="normal" required>
              <Select
                labelId="type-label"
                id="type"
                name="type"
                value={newChallenge.type}
                label={t('leaderboard:challenges.form.challengeType')}
                onChange={handleInputChange}
              >
                <MenuItem value="xp">
                  {t('leaderboard:challenges.types.xp')}
                </MenuItem>
                <MenuItem value="courses">
                  {t('leaderboard:challenges.types.courses')}
                </MenuItem>
                <MenuItem value="tests">
                  {t('leaderboard:challenges.types.tests')}
                </MenuItem>
              </Select>
            </FormControl>

            <TextField
              margin="normal"
              required
              fullWidth
              id="goal"
              label={t('leaderboard:challenges.form.goalLabel')}
              name="goal"
              type="number"
              value={newChallenge.goal}
              onChange={handleInputChange}
              error={!!formErrors.goal}
              helperText={
                formErrors.goal ||
                `${t('leaderboard:challenges.form.goalHelper')} ${
                  newChallenge.type === 'xp'
                    ? 'XP'
                    : newChallenge.type === 'courses'
                    ? t('leaderboard:challenges.types.courses').toLowerCase()
                    : t('leaderboard:challenges.types.tests').toLowerCase()
                }`
              }
            />

            <TextField
              margin="normal"
              required
              fullWidth
              id="duration"
              label={t('leaderboard:challenges.form.durationLabel')}
              name="duration"
              type="number"
              value={newChallenge.duration}
              onChange={handleInputChange}
              error={!!formErrors.duration}
              helperText={formErrors.duration}
            />

            <TextField
              margin="normal"
              fullWidth
              multiline
              rows={3}
              id="description"
              label={t('leaderboard:challenges.form.descriptionLabel')}
              name="description"
              value={newChallenge.description}
              onChange={handleInputChange}
              helperText={t('leaderboard:challenges.form.descriptionHelper')}
            />
          </Box>
        </DialogContent>

        <DialogActions>
          <Button variant="text" onClick={handleCreateDialogClose}>
            {t('leaderboard:challenges.form.cancel')}
          </Button>
          <Button
            onClick={handleCreateChallenge}
            variant="contained"
            color="primary"
            disabled={loading}
          >
            {loading
              ? t('leaderboard:challenges.form.creating')
              : t('leaderboard:challenges.form.send')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Challenges;
